import { ProductsWithHoldingsAndRates } from 'hooks/products/useProducts';
import {
  RFQTradeQuoteDataDto,
  RFQTradeQuoteSelectionDataDto,
} from 'services/generated';
import { formatCurrency } from 'utils/currencies';

interface CalculatedTradeData {
  bothAssetsSelected: boolean;
  hasError: boolean;
  fromHasError: boolean;
  toAmount: number | null;
  lastKnownPrice: number;
  quoteResultPrice: number | null;
  exceedsBalanceError: boolean;
  belowMinimumError: boolean;
  aboveMaximumError: boolean;
  balanceBelowMinimumError: boolean;
  minimumFromValue: number | null;
  maximumFromValue: number | null;
}

export function calculateTradeData(
  tradeCompleted: boolean,
  type: 'buy' | 'sell',
  fromAssetCode: string | null,
  toAssetCode: string | null,
  initiateQuoteResult?: RFQTradeQuoteDataDto,
  quoteData?: RFQTradeQuoteSelectionDataDto,
  fromAmount?: number | null,
  fromProductAvailableAmount?: number,
): CalculatedTradeData {
  const bothAssetsSelected = Boolean(fromAssetCode && toAssetCode);

  // Ensure that initiateQuoteResult is defined before accessing its properties
  const quoteResultPrice = initiateQuoteResult
    ? type === 'buy'
      ? 1 / initiateQuoteResult.approximateFromToPrice!
      : initiateQuoteResult.approximateFromToPrice!
    : null;

  const lastKnownPrice = quoteData
    ? type === 'buy'
      ? 1 / quoteData.approximateFromProductToToProductRate!
      : quoteData.approximateFromProductToToProductRate!
    : 0;

  const toAmount =
    fromAmount && quoteData
      ? fromAmount * quoteData.approximateFromProductToToProductRate
      : null;

  const approximateFiatValue =
    fromAmount && quoteData
      ? fromAmount / quoteData.approximateFunctionalToFromProductRate
      : null;

  const balanceApproximateFiatValue =
    fromProductAvailableAmount && quoteData
      ? fromProductAvailableAmount /
        quoteData.approximateFunctionalToFromProductRate
      : null;

  const minimumFromValue = quoteData
    ? quoteData.approximateFunctionalToFromProductRate * quoteData.minimumAmount
    : null;

  const maximumFromValue =
    quoteData && quoteData.maximumAmount
      ? quoteData.approximateFunctionalToFromProductRate *
        quoteData.maximumAmount
      : null;

  const exceedsBalanceError = fromAmount! > fromProductAvailableAmount!;

  const belowMinimumError =
    fromAmount && approximateFiatValue && quoteData
      ? approximateFiatValue < quoteData.minimumAmount
      : false;

  const aboveMaximumError =
    fromAmount && quoteData && quoteData.maximumAmount && approximateFiatValue
      ? approximateFiatValue > quoteData.maximumAmount
      : false;

  const balanceBelowMinimumError = Boolean(
    quoteData &&
      balanceApproximateFiatValue &&
      balanceApproximateFiatValue < quoteData.minimumAmount,
  );

  const fromHasError =
    quoteData?.fromProductCode === fromAssetCode &&
    Boolean(
      balanceBelowMinimumError ||
        (fromAmount &&
          (exceedsBalanceError || belowMinimumError || aboveMaximumError)),
    ) &&
    !tradeCompleted;

  const hasError = Boolean(!bothAssetsSelected || !fromAmount || fromHasError);

  return {
    bothAssetsSelected,
    hasError,
    fromHasError,
    toAmount,
    lastKnownPrice,
    quoteResultPrice,
    exceedsBalanceError,
    belowMinimumError,
    aboveMaximumError,
    balanceBelowMinimumError,
    minimumFromValue,
    maximumFromValue,
  };
}
